// chat/actions.ts — the chat control executor for Muster (cloned from Oriora Lockbox's src/chat/actions.ts).
// The client router (chat/brain.ts) maps ONE user message → ONE ChatAction; this file EXECUTES that
// action against the on-device store (src/store.ts). Everything here is LOCAL SQLite over Tauri IPC —
// no network, no billing. The ONE AI-billed step (the collection recap) happens in the router via the
// EXISTING live endpoint (recapCollection → /api/apps/oriora_muster/recap), never here — the executor
// only renders the pre-fetched recap text (see chat/brain.ts).
//
// Actions are derived from THIS app's own store: create/update/delete for EVERY tap-able entity —
// units, armies (factions), paints and recipes — per the CHAT-AGENT-STANDARD capability-completeness
// rule (2026-08-12): anything the customer can do by tapping must be do-able by asking. (This
// supersedes the earlier "units-only, factions/paints/recipes stay in the Collection tab" scoping.)
//
// HITL discipline: the destructive actions (delete_*) are NEVER executed from a chat turn on their
// own — the Chat UI holds them and requires an explicit Confirm tap first.

import { store, statusLabel, type Status,
  type NewFaction, type FactionPatch, type UnitPatch,
  type NewPaint, type PaintPatch, type NewRecipe, type RecipePatch } from "../store";
import { exportBackup, importBackup } from "../backup";
import { invoke } from "../tauri-web";
import { saveCopyNow, importFromFile } from "../localFolder";
const LOCAL_SLUG = "muster";
import { cloudBackupSave, cloudBackupLoad, cloudBackupStatus, cloudStoreSave, BrainError } from "../brain";
import { addInstruction, resolveFaction, findUnitByName } from "./brain";
import { libraryAdd, libraryList, libraryRemove, libraryMove, libraryMerge } from "../library";

// A compact result row rendered inline in a chat bubble (unit search matches).
export interface ResultRow {
  id: string;
  primary: string;
  secondary?: string;
  trailing?: string;
}

export type ChatAction =
  | { kind: "none"; message?: string }
  | { kind: "add_unit"; factionId: string; factionLabel: string; factionName?: string; name: string; points?: number; modelCount?: number }
  | { kind: "set_status"; id: string; unitName?: string; label: string; status: Status } // a unit's status
  | { kind: "edit_unit"; id: string; patch: UnitPatch; label?: string } // rename / points / models / notes / re-file
  | { kind: "delete_unit"; id: string; label?: string } // UI confirms before this reaches executeAction
  | { kind: "search_units"; query?: string; status?: Status; factionId?: string; factionLabel?: string }
  // ── armies (factions) ──
  | { kind: "add_faction"; input: NewFaction }
  | { kind: "edit_faction"; id: string; patch: FactionPatch; label?: string }
  | { kind: "delete_faction"; id: string; label?: string } // UI confirms (removes the army + its units)
  // ── paint inventory ──
  | { kind: "add_paint"; input: NewPaint }
  | { kind: "edit_paint"; id: string; patch: PaintPatch; label?: string }
  | { kind: "delete_paint"; id: string; label?: string } // UI confirms
  // ── paint recipes ──
  | { kind: "add_recipe"; input: NewRecipe }
  | { kind: "edit_recipe"; id: string; patch: RecipePatch; label?: string }
  | { kind: "delete_recipe"; id: string; label?: string } // UI confirms
  | { kind: "recap"; text: string } // pre-fetched by the router's ONE billed call — rendered only
  | { kind: "backup_save" }
  | { kind: "backup_status" }
  | { kind: "backup_restore"; provider?: "github" | "gdrive" } // UI confirms before executeAction (replaces all data)
  | { kind: "save_to_cloud"; contentKind: "conversation" | "note"; note?: string; destination?: "github" | "gdrive" }
  | { kind: "save_local_copy" }
  | { kind: "import_file" }
  | { kind: "remember"; instruction: string }
  // ── ARTIFACT TOOLS — the customer's library (fleet-wide) ────────────────────
  | { kind: "library_save"; title: string; itemKind: "lesson" | "plan" | "note"; content: string; folder?: string }
  | { kind: "library_list"; folder?: string }
  | { kind: "library_remove"; id: string; label?: string }   // UI confirms first
  | { kind: "library_move"; id: string; folder: string; label?: string }
  | { kind: "library_merge"; ids: string[]; title: string; label?: string };  // UI confirms first

export interface ActionResult {
  ok: boolean;
  message: string;    // shown back in the chat
  changed?: boolean;  // units changed → the main screen should refresh
  rows?: ResultRow[]; // matches to render inline
}

export async function executeAction(a: ChatAction, ctx?: { transcript?: string }): Promise<ActionResult> {
  try {
    switch (a.kind) {
      case "none":
        return { ok: true, message: a.message ?? "" };

      case "add_unit": {
        // Resolution may have deferred the army (created by a sibling action this turn).
        let factionId = a.factionId;
        if (!factionId && a.factionName) {
          const fr = await resolveFaction(a.factionName);
          if (!fr.ok) return { ok: false, message: fr.message };
          factionId = fr.faction.id;
        }
        if (!factionId) return { ok: false, message: "Which army is this unit for?" };
        const u = await store.createUnit({
          faction_id: factionId,
          name: a.name,
          ...(a.points !== undefined ? { points: a.points } : {}),
          ...(a.modelCount !== undefined ? { model_count: a.modelCount } : {}),
        });
        const bits = `${u.points} pts · ${u.model_count} model${u.model_count !== 1 ? "s" : ""}`;
        return { ok: true, message: `Added “${u.name}” to ${a.factionLabel} (${bits}).`, changed: true };
      }

      case "set_status": {
        // Resolution may have deferred the unit (created by a sibling action this turn).
        let unitId = a.id;
        if (!unitId && a.unitName) {
          const rows = await findUnitByName(a.unitName);
          if (rows.length !== 1) return { ok: false, message: `Couldn't find "${a.unitName}".` };
          unitId = rows[0].id;
        }
        if (!unitId) return { ok: false, message: "Which unit should I update?" };
        await store.updateUnit(unitId, { status: a.status });
        return { ok: true, message: `Marked ${a.label} as ${statusLabel(a.status)}.`, changed: true };
      }

      case "edit_unit":
        await store.updateUnit(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the unit"}.`, changed: true };

      case "delete_unit":
        await store.deleteUnit(a.id);
        return { ok: true, message: `Removed ${a.label ?? "the unit"} from your collection.`, changed: true };

      // ── armies (factions) ──
      case "add_faction": {
        const f = await store.createFaction(a.input);
        return { ok: true, message: `Added army “${f.name}”${f.game_system ? ` (${f.game_system})` : ""}.`, changed: true };
      }
      case "edit_faction":
        await store.updateFaction(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the army"}.`, changed: true };
      case "delete_faction":
        await store.deleteFaction(a.id);
        return { ok: true, message: `Removed ${a.label ?? "the army"} and its units.`, changed: true };

      // ── paint inventory ──
      case "add_paint": {
        const pt = await store.createPaint(a.input);
        return { ok: true, message: `Added paint “${pt.name}”${pt.brand ? ` (${pt.brand})` : ""}.`, changed: true };
      }
      case "edit_paint":
        await store.updatePaint(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the paint"}.`, changed: true };
      case "delete_paint":
        await store.deletePaint(a.id);
        return { ok: true, message: `Removed ${a.label ?? "the paint"}.`, changed: true };

      // ── paint recipes ──
      case "add_recipe": {
        const rc = await store.createRecipe(a.input);
        return { ok: true, message: `Added recipe “${rc.name}”.`, changed: true };
      }
      case "edit_recipe":
        await store.updateRecipe(a.id, a.patch);
        return { ok: true, message: `Updated ${a.label ?? "the recipe"}.`, changed: true };
      case "delete_recipe":
        await store.deleteRecipe(a.id);
        return { ok: true, message: `Removed ${a.label ?? "the recipe"}.`, changed: true };

      case "search_units": {
        const units = await store.listUnits(a.factionId);
        const q = a.query?.toLowerCase();
        const matches = units.filter(
          (u) => (!q || u.name.toLowerCase().includes(q)) && (!a.status || u.status === a.status)
        );
        const rows: ResultRow[] = matches.slice(0, 20).map((u) => ({
          id: u.id,
          primary: u.name,
          secondary: `${u.points} pts · ${u.model_count} model${u.model_count !== 1 ? "s" : ""}`,
          trailing: statusLabel(u.status),
        }));
        const scope = [a.status ? statusLabel(a.status) : null, a.factionLabel ?? null].filter(Boolean).join(" · ");
        return {
          ok: true,
          message: matches.length
            ? `${matches.length} unit${matches.length !== 1 ? "s" : ""}${scope ? ` — ${scope}` : ""}.`
            : `No units found${scope ? ` for ${scope}` : ""}.`,
          rows,
        };
      }

      case "recap":
        return { ok: true, message: a.text || "Nothing to recap yet." };

      case "backup_save": {
        let provider: string;
        try {
          ({ provider } = await cloudBackupSave(JSON.parse(await exportBackup())));
        } catch (e) {
          // A backup past the standard path's size limit → the device-direct GitHub
          // save (any size, straight from this device, never through Oriora). Chat can DO this
          // one itself when GitHub is already connected in the backup menu; it can't run the
          // one-time OAuth login, so when not connected it points there by name instead.
          if (e instanceof BrainError && e.code === "too_large") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected) {
              await invoke("backup_to_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I saved it straight from this device to your GitHub — any size works that way, and it never passes through Oriora." };
            }
            return { ok: false, message: 'This backup is too large for the standard path. One-time fix: open the backup menu (☁ icon, top right) → "Log in with GitHub" — after that, large backups save directly from this device, and I can run them for you from chat.' };
          }
          throw e;
        }
        const name = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "cloud";
        return { ok: true, message: "Backed up your whole collection to your " + name + " — restore it on any device, any time." };
      }

      case "backup_status": {
        // The RESTORE STORY in words — same ground truth the panel's status line shows.
        const s = await cloudBackupStatus();
        if (s.connected.length === 0) {
          const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
          if (deviceGh) {
            return { ok: true, message: "GitHub is logged in on this device (backup menu), so backups save and restore directly from here — any size, never passing through Oriora. Optionally, connecting a cloud at orioralabs.com → Settings → Connections adds the everyday path all your Oriora apps share." };
          }
          return { ok: true, message: 'Nothing is connected yet. Two options: connect GitHub or Google Drive once at orioralabs.com → Settings → Connections (all your Oriora apps back up there), or open the backup menu here (☁ icon) → "Log in with GitHub" — that one stays on this device only, and handles any size.' };
        }
        const name = (p: string) => (p === "gdrive" ? "Google Drive" : "GitHub");
        const when = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : null);
        const lines: string[] = [];
        const w = s.writes_to;
        if (w) {
          const slot = s.slots[w];
          lines.push(`Your backups write to ${name(w)} (connected in your Oriora account)` +
            (slot?.backup_exists ? ` — last backup ${when(slot.updated_at)}.` : " — no backup saved yet."));
        }
        for (const p of s.connected.filter((c) => c !== w)) {
          const slot = s.slots[p];
          lines.push(`${name(p)} is also connected but not used for new backups (${name(w!)} takes priority)` +
            (slot?.backup_exists ? ` — it still holds an older backup from ${when(slot.updated_at)}.` : "."));
        }
        const deviceGh = await invoke<boolean>("has_github_restore_token").catch(() => false);
        if (deviceGh) {
          lines.push("GitHub is also logged in on this device, so large backups save and restore directly from here — any size, never through Oriora.");
        }
        lines.push('Say "restore my collection" and I\'ll read the backup back in — or name a source, like "restore from Drive".');
        return { ok: true, message: lines.join("\n") };
      }

      case "backup_restore": {
        const src = a.provider ? (a.provider === "gdrive" ? "Google Drive" : "GitHub") : "cloud";
        let st;
        try {
          st = await cloudBackupLoad(a.provider);
        } catch (e) {
          // Large-backup handoff: if GitHub is connected on this device, chat pulls it directly
          // itself (the user already confirmed the REPLACE before this executes); it can't run the
          // one-time OAuth login, so when not connected it points at the button by name instead.
          if (e instanceof BrainError && e.code === "unavailable") {
            const connected = await invoke<boolean>("has_github_restore_token").catch(() => false);
            if (connected && (!a.provider || a.provider === "github")) {
              await invoke("restore_from_github_token");
              return { ok: true, message: "Your backup was too large for the standard path, so I pulled it straight from your GitHub onto this device — your data is back.", changed: true };
            }
            return { ok: false, message: `Reading your ${src} backup through Oriora didn't work — if your backup is large, use the backup menu (☁ icon, top right) → "Log in with GitHub", which backs up and restores any size directly on this device.` };
          }
          throw e;
        }
        if (!st.connected) return { ok: false, message: "Connect GitHub or Google Drive in your Oriora account first (Settings → Service connections)." };
        if (!st.data) return { ok: false, message: a.provider ? `No backup found in your ${src} yet.` : 'No cloud backup found yet — say "back up" first.' };
        await importBackup(JSON.stringify(st.data));
        return { ok: true, message: `Restored from your ${st.provider === "gdrive" ? "Google Drive" : "GitHub"} backup.`, changed: true };
      }

      case "save_to_cloud": {
        // Saves the CURRENT conversation (or a note) to the customer's OWN cloud — distinct from
        // backup_save (the whole collection). Text relay only; Oriora retains nothing.
        const content = a.contentKind === "conversation" ? (ctx?.transcript ?? "") : (a.note ?? "");
        if (!content.trim()) {
          return { ok: false, message: a.contentKind === "conversation" ? "There's nothing to save yet — chat with me a bit first, then ask again." : "Tell me what to save and I'll store it." };
        }
        const { provider } = await cloudStoreSave(content, { name: a.contentKind, kind: a.contentKind, destination: a.destination });
        const where = provider === "github" ? "GitHub" : provider === "gdrive" ? "Google Drive" : "your cloud";
        const what = a.contentKind === "conversation" ? "our conversation" : "your note";
        return { ok: true, message: `Saved ${what} to your ${where} — it's yours to keep.` };
      }

      case "save_local_copy": {
        const saved = await saveCopyNow(LOCAL_SLUG);
        return saved
          ? { ok: true, message: "Saved a copy of your data to the folder you picked — it's yours to keep or move." }
          : { ok: true, message: "Cancelled — no copy saved." };
      }

      case "import_file": {
        const json = await importFromFile();
        if (json === null) return { ok: true, message: "No file chosen — nothing imported." };
        await importBackup(json);
        return { ok: true, message: "Imported your data from the file — this device now matches that backup.", changed: true };
      }

      case "remember": {
        addInstruction(a.instruction);
        return { ok: true, message: "Noted — from now on: " + a.instruction };
      }

      // ── ARTIFACT TOOLS (the library) — device-only, fleet-wide ──────────────
      case "library_save": {
        const _item = libraryAdd(a.title, a.itemKind, a.content, a.folder ?? "");
        return { ok: true, message: `Saved "${_item.title}" to your library${_item.folder ? ` (folder: ${_item.folder})` : ""} — on this device, yours.`, changed: true };
      }
      case "library_list": {
        const _items = libraryList(a.folder);
        if (!_items.length) return { ok: true, message: a.folder ? `Nothing in "${a.folder}" yet.` : "Your library is empty — ask me to write you something and I'll keep it here." };
        return { ok: true, message: (a.folder ? `In "${a.folder}":\n` : "Your library:\n") + _items.map((i) => `• ${i.title} — ${i.kind}${i.folder ? ` · ${i.folder}` : ""}`).join("\n") };
      }
      case "library_remove":
        return libraryRemove(a.id)
          ? { ok: true, message: `Removed ${a.label ?? "it"} from your library.`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_move":
        return libraryMove(a.id, a.folder)
          ? { ok: true, message: `Moved ${a.label ?? "it"} to "${a.folder}".`, changed: true }
          : { ok: false, message: "That item isn't in your library any more." };
      case "library_merge": {
        const _merged = libraryMerge(a.ids, a.title);
        return _merged
          ? { ok: true, message: `Merged into "${_merged.title}" — the originals are folded in.`, changed: true }
          : { ok: false, message: "I need at least two existing items to merge." };
      }


      default:
        return { ok: false, message: "I didn't catch that — try again?" };
    }
  } catch (e) {
    return { ok: false, message: `That didn't work: ${e instanceof Error ? e.message : String(e)}` };
  }
}
